import React from 'react';
import { motion, Variants } from 'framer-motion';

interface FadeInProps {
  children: React.ReactNode;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Animation duration (in seconds) */
  duration?: number;
  /** Apply animation only once (when component mounts) */
  once?: boolean;
  /** Custom className for styling */
  className?: string;
}

/**
 * FadeIn - Simple fade-in animation wrapper
 *
 * Fades content from opacity 0 to 1 with Material Design easing curve.
 * Respects prefers-reduced-motion accessibility preference.
 *
 * @example
 * ```tsx
 * <FadeIn delay={0.2} duration={0.5}>
 *   <YourComponent />
 * </FadeIn>
 * ```
 */
export const FadeIn: React.FC<FadeInProps> = ({
  children,
  delay = 0,
  duration = 0.4,
  once = true,
  className,
}) => {
  const variants: Variants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration,
        delay,
        ease: [0.4, 0.0, 0.2, 1], // Material Design easing
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={variants}
      className={className}
      // Disable animation if user prefers reduced motion
      style={{ willChange: 'opacity' }}
    >
      {children}
    </motion.div>
  );
};
